/* -------------------------- Design imports start -------------------------- */
import { Box, Chip, Grid, Stack, Typography, Button as MUIButton, Collapse, IconButton } from '@mui/material'
import { ExpandMore, ExpandLess } from '@mui/icons-material'
import { Table } from 'antd'
import React, { useState } from 'react'
/* --------------------------- Design imports end --------------------------- */

/* ------------------------ Functional imports start ------------------------ */
import moment from 'moment'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router-dom'

import Button from '../../../../../components/Button'
import InfoField from '../../../../../components/inputs/InfoField'
import InfoPanel from '../../../../../components/layout/InfoPanel'
import FieldLabel from '../../../../../components/widgets/FieldLabel'
import LogTool from '../../../../../logger/logTools'
import { useUserContext } from '../../../../../utils/context'
import {
  getContactRepresentation,
  getOrgUserRepresentation,
  getPKfromSelf,
  isPrivileged,
} from '../../../../../utils/functions'
import { type OrganizationUser, type User } from '../../../../../utils/types'
import { parseNumericPrice } from '../../../utils/functions'
/* ------------------------- Functional imports end ------------------------- */

type Props = {
  open: boolean
  setOpen: (open: boolean) => void
  setOpenFilesDrawer: (open: boolean) => void
  infoData: any
  title?: string
  disabled?: boolean
}

type ContentProps = {
  setOpenFilesDrawer: (open: boolean) => void
  infoData: any
}

export function RequestInfoPanelContent(props: ContentProps) {
  /* -------------------------- Non state data start -------------------------- */
  const { setOpenFilesDrawer, infoData } = props
  const log = new LogTool({ context: 'RequestInfoPanel', enable: true, logLevel: 'debug' })
  const { t } = useTranslation()
  const { user } = useUserContext()
  const history = useHistory()

  log.debug('infoData -> ', infoData)
  /* --------------------------- Non state data end --------------------------- */

  /* ---------------------------- Flag states start --------------------------- */
  const [calculatedCostsExpanded, setCalculatedCostsExpanded] = useState(false)
  const [costBreakdownExpanded, setCostBreakdownExpanded] = useState(false)
  /* ----------------------------- Flag states end ---------------------------- */

  /* ---------------------------- Data states start --------------------------- */
  /* ----------------------------- Data states end ---------------------------- */

  /* ------------------------------ Effects start ----------------------------- */
  /* ------------------------------- Effects end ------------------------------ */

  /* ------------------------- Utility functions start ------------------------ */
  /**
   * Maps cost breakdown keys to translation keys for display
   */
  const getCostBreakdownLabel = (key: string): string => {
    const keyMap: { [key: string]: string } = {
      'estimated_price': t('request:content.label.estimatedPrice'),
      'estimated_material_cost': t('request:content.label.estimatedMaterialCost'),
      'estimated_manufacturing_cost': t('request:content.label.estimatedManufacturingCost'),
      'estimated_treatment_cost': t('request:content.label.estimatedTreatmentCost'),
      'estimated_investment_cost': t('request:content.label.estimatedInvestmentCost'),
      'remodelling_cost': t('request:content.label.remodellingCost'),
    }
    return keyMap[key] || key
  }

  /**
   * Format cost value nicely with German locale
   */
  const formatCostValue = (value: unknown): string => {
    const parsed = parseNumericPrice(value as string | number | undefined)
    if (parsed === null) return String(value)
    return parsed.toLocaleString('de-DE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }) + ' €'
  }
  /* -------------------------- Utility functions end ------------------------- */

  /* ------------------------ Callback functions start ------------------------ */
  /* ------------------------- Callback functions end ------------------------- */

  /* ------------------------- Render constants start ------------------------- */
  const renderStatus = (status: number): React.ReactNode => {
    switch (status) {
      case 0:
        return (
          <Stack direction="row" spacing={1}>
            <Chip label={t('request:content.label.pending')} color="warning" variant="outlined" />
          </Stack>
        )
      case 1:
        return (
          <Stack direction="row" spacing={1}>
            <Chip label={t('request:content.label.accepted')} color="success" variant="outlined" />
          </Stack>
        )
      case 2:
        return (
          <Stack direction="row" spacing={1}>
            <Chip label={t('request:content.label.rejected')} color="error" variant="outlined" />
          </Stack>
        )
      case 3:
        return (
          <Stack direction="row" spacing={1}>
            <Chip label={t('request:content.label.forwarded')} color="primary" variant="outlined" />
          </Stack>
        )
      default:
        return (
          <Stack direction="row" spacing={1}>
            <Chip label={t('request:content.label.unknown')} color="default" variant="outlined" />
          </Stack>
        )
    }
  }

  let urgency = ''
  if (infoData) {
    if (infoData.wishDate && infoData.priority) {
      urgency = t('request:content.label.deadline')
    }
    if (infoData.wishDate && !infoData.priority) {
      urgency = t('request:content.label.wishDate')
    }
    if (!infoData.wishDate && infoData.priority) {
      urgency = t('request:content.label.urgent')
    }
    if (!infoData.wishDate && !infoData.priority) {
      urgency = t('request:content.label.nonUrgent')
    }
  }
  /* -------------------------- Render constants end -------------------------- */

  /* ------------------------ Pre render actions start ------------------------ */
  if (infoData) {
    // Only check article properties if article exists and is not null
    if (infoData.article !== null && infoData.article !== undefined) {
      if (typeof infoData.article === 'string') {
        throw new Error(
          'RequestInfoPanel expects the requested Article to be available through the ' +
            'provided infoData (Request).'
        )
      }
      if (typeof infoData.article.properties === 'string') {
        throw new Error(
          'RequestInfoPanel expects the Properties of the requested Article to be ' +
            'available through infoData.article. Make sure to include it with ' +
            'the requested Article.'
        )
      }
    }
  }
  /* ------------------------- Pre render actions end ------------------------- */

  log.debug('infoData ->', infoData)

  /* -------------------------------------------------------------------------- */
  /*                              Render Component                              */
  /* -------------------------------------------------------------------------- */

  log.debug('InfoData -> ', infoData)
  return (
    <>
      <Typography variant="h6" ml="20px" mt="10px">
        {t('request:content.heading.processingStatus')}
      </Typography>
      <Stack spacing={2} padding="20px">
        {isPrivileged(user, 'STAFF') && (
          <>
            <Box>
              <FieldLabel value={t('common:content.label.editors')} />
              {infoData?.editors && infoData.editors.length > 0 ? (
                <Box display="flex" sx={{ mt: '16px' }}>
                  {/* eslint-disable-next-line @typescript-eslint/no-unsafe-call */}
                  {infoData.editors.map((editor: User) => (
                    <Chip
                      key={editor.key}
                      label={getOrgUserRepresentation(editor as OrganizationUser)}
                      sx={{ ml: '10px' }}
                    />
                  ))}
                </Box>
              ) : (
                <Typography variant="body1" p="14px 15px">
                  -
                </Typography>
              )}
            </Box>
            <InfoField
              label={t('common:content.label.remarks')}
              fullWidth
              value={infoData?.remarks}
            />
          </>
        )}
        <InfoField
          label={t('common:content.label.status')}
          fullWidth
          value={
            renderStatus(infoData?.status)
          }
        />
      </Stack>
      <Typography variant="h6" ml="20px" mt="10px">
        {t('common:content.label.generalInformation')}
      </Typography>
      <Grid container spacing={2} style={{ padding: '20px' }}>
        <Grid item xs={12}>
          <InfoField
            label={t('request:content.label.requestNumber')}
            fullWidth
            value={infoData?.self && getPKfromSelf(infoData.self)}
          />
        </Grid>
        <Grid item xs={12}>
          <InfoField
            label={t('common:content.label.article')}
            fullWidth
            value={infoData?.article?.name}
          />
        </Grid>
        <Grid item xs={12}>
          <InfoField
            label={t('request:content.label.quantities')}
            fullWidth
            /* eslint-disable-next-line @typescript-eslint/no-unsafe-call */
            value={infoData?.quantities.join(',')}
          />
        </Grid>
        <Grid item xs={12}>
          <InfoField
            label={t('common:content.label.description')}
            fullWidth
            value={infoData?.description}
          />
        </Grid>
        <Grid item xs={12}>
          <InfoField
            label={t('common:content.label.certificates')}
            fullWidth
            value={infoData?.certificate}
          />
        </Grid>
        <Grid item xs={12}>
          <InfoField
            label={t('common:content.label.createdAt')}
            fullWidth
            value={moment(infoData?.createdAt).format('DD.MM.YYYY')}
          />
        </Grid>
        <Grid item xs={12}>
          <InfoField
            label={t('common:content.label.lastModifiedAt')}
            fullWidth
            value={moment(infoData?.lastModifiedAt).format('DD.MM.YYYY')}
          />
        </Grid>
        <Grid item xs={12}>
          <InfoField label={t('request:content.label.urgency')} value={urgency} />
        </Grid>
        {infoData?.wishDate && (
          <Grid item xs={12}>
            <InfoField label={urgency} value={moment(infoData?.wishDate).format('DD.MM.YYYY')} />
          </Grid>
        )}
      </Grid>
      {/* Contact Person - Staff only */}
      {isPrivileged(user, 'STAFF') && infoData?.contact && (
        <>
          <Typography variant="h6" ml="20px" mt="10px">
            {t('common:content.label.contactPerson')}
          </Typography>
          <Stack spacing={2} p="20px">
            <InfoField
              label={t('common:content.label.name')}
              value={getContactRepresentation(infoData.contact)}
            />
            <InfoField label={t('common:content.label.email')} value={infoData.contact.email} />
          </Stack>
        </>
      )}

      {/* Cost Report - Visible to all users */}
      <Typography variant="h6" ml="20px" mt="10px">
        {t('request:content.label.costReport')}
      </Typography>
      <Grid container spacing={2} style={{ padding: '20px' }}>
        {/* Customer Price Suggestion - Visible to all users */}
        <Grid item xs={12}>
          <InfoField
            label={t('request:content.label.customerPriceSuggestion')}
            fullWidth
            value={(() => {
              if (!infoData?.customerPriceSuggestion) return '-'
              const numValue = parseFloat(infoData.customerPriceSuggestion)
              if (isNaN(numValue)) return `${infoData.customerPriceSuggestion} €`
              const formattedValue = numValue.toLocaleString('de-DE', {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })
              return `${formattedValue} €`
            })()}
          />
        </Grid>

        {/* Calculated Costs - Visible to all users, expandable */}
        {infoData?.calculatedCosts && Object.keys(infoData.calculatedCosts).length > 0 && (
          <Grid item xs={12}>
            <Box>
              <Box 
                display="flex" 
                alignItems="center" 
                onClick={() => setCalculatedCostsExpanded(!calculatedCostsExpanded)}
                sx={{ 
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'action.hover' },
                  borderRadius: 1,
                  py: 0.5,
                  px: 1,
                  ml: -1,
                }}
              >
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontWeight: 500, 
                    color: 'text.secondary',
                    flexGrow: 1,
                  }}
                >
                  {t('request:content.label.calculatedCosts')}
                </Typography>
                <IconButton size="small" sx={{ ml: 1 }}>
                  {calculatedCostsExpanded ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              </Box>
              <Collapse in={calculatedCostsExpanded}>
                <Box sx={{ mt: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Table
                    size="small"
                    columns={[
                      {
                        title: t('common:content.label.quantity'),
                        dataIndex: 'quantity',
                        key: 'quantity',
                      },
                      {
                        title: t('request:content.label.price'),
                        dataIndex: 'price',
                        key: 'price',
                      },
                    ]}
                    dataSource={Object.entries(infoData.calculatedCosts).map(([quantityKey, costValue], index) => {
                      const quantity = parseFloat(quantityKey)
                      // Use parseNumericPrice to handle strings like "€ 65.00"
                      const totalCost = parseNumericPrice(costValue as string | number | undefined)
                      
                      // Format with German locale
                      const formatPrice = (value: number) => value.toLocaleString('de-DE', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })
                      
                      if (totalCost === null) {
                        return {
                          key: index,
                          quantity: `${quantityKey} Stk.`,
                          price: t('request:content.label.costsUnknown'),
                        }
                      }                      
                      return {
                        key: index,
                        quantity: `${isNaN(quantity) ? quantityKey : quantity.toLocaleString('de-DE')} Stk.`,
                        price: `${formatPrice(totalCost)} €`,
                      }
                    })}
                    pagination={false}
                  />
                </Box>
              </Collapse>
            </Box>
          </Grid>
        )}

        {/* Calculated Cost Breakdown - Staff only, expandable */}
        {isPrivileged(user, 'STAFF') && infoData?.calculatedCostBreakdown && 
          Object.keys(infoData.calculatedCostBreakdown).length > 0 && (
          <Grid item xs={12}>
            <Box>
              <Box 
                display="flex" 
                alignItems="center" 
                onClick={() => setCostBreakdownExpanded(!costBreakdownExpanded)}
                sx={{ 
                  cursor: 'pointer',
                  '&:hover': { bgcolor: 'action.hover' },
                  borderRadius: 1,
                  py: 0.5,
                  px: 1,
                  ml: -1,
                }}
              >
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontWeight: 500, 
                    color: 'text.secondary',
                    flexGrow: 1,
                  }}
                >
                  {t('request:content.label.calculatedCostBreakdown')}
                </Typography>
                <IconButton size="small" sx={{ ml: 1 }}>
                  {costBreakdownExpanded ? <ExpandLess /> : <ExpandMore />}
                </IconButton>
              </Box>
              <Collapse in={costBreakdownExpanded}>
                <Box sx={{ mt: 1 }}>
                  {Object.entries(infoData.calculatedCostBreakdown).map(([quantity, breakdown]: [string, any]) => (
                    <Box key={quantity} sx={{ mb: 2, p: 1, bgcolor: 'grey.50', borderRadius: 1 }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 'bold', mb: 1 }}>
                        {t('request:content.label.quantity')}: {quantity} Stk.
                      </Typography>
                      {breakdown && typeof breakdown === 'object' && (
                        <Table
                          size="small"
                          columns={[
                            {
                              title: t('request:content.label.costType', { defaultValue: 'Kostenart' }),
                              dataIndex: 'type',
                              key: 'type',
                            },
                            {
                              title: t('common:content.label.value', { defaultValue: 'Wert' }),
                              dataIndex: 'value',
                              key: 'value',
                            },
                          ]}
                          dataSource={Object.entries(breakdown).map(([key, value]: [string, any]) => ({
                            key,
                            type: getCostBreakdownLabel(key),
                            value: formatCostValue(value),
                          }))}
                          pagination={false}
                        />
                      )}
                    </Box>
                  ))}
                </Box>
              </Collapse>
            </Box>
          </Grid>
        )}

        {/* Reported Costs - Staff only */}
        {isPrivileged(user, 'STAFF') && (
          <Grid item xs={12}>
            <InfoField
              label={t('request:content.label.reportedCosts')}
              fullWidth
              value={
                infoData?.quantities && infoData?.reportedCosts && 
                  Object.keys(infoData.reportedCosts).length > 0 ? (
                  <Table
                    columns={[
                      {
                        title: t('common:content.label.quantity'),
                        dataIndex: 'quantity',
                        key: 'quantity',
                      },
                      {
                        title: t('request:content.label.reportedCosts'),
                        dataIndex: 'reportedCosts',
                        key: 'reportedCosts',
                      },
                    ]}
                    /* eslint-disable-next-line @typescript-eslint/no-unsafe-call */
                    dataSource={infoData.quantities.map((quantity: number) => {
                      let lowestReportedCosts: number | undefined = undefined
                      for (const supplier of Object.keys(infoData.reportedCosts)) {
                        if (
                          typeof lowestReportedCosts === 'undefined' ||
                          lowestReportedCosts > infoData.reportedCosts[supplier][quantity]
                        ) {
                          lowestReportedCosts = infoData.reportedCosts[supplier][quantity]
                        }
                      }
                      return {
                        key: `reported_costs_${quantity}`,
                        // eslint-disable-next-line @typescript-eslint/restrict-plus-operands
                        quantity: quantity + ' Stk.',
                        reportedCosts:
                          typeof lowestReportedCosts !== 'undefined'
                            ? `ab ${lowestReportedCosts} €`
                            : t('request:content.label.costsUnknown'),
                      }
                    })}
                    pagination={false}
                  />
                ) : '-'
              }
            />
          </Grid>
        )}

        <Grid item xs={12}>
          <Button
            id="showFilesButton"
            fullWidth
            variant="contained"
            onClick={() => {
              setOpenFilesDrawer(true)
            }}
          >
            {t('common:interaction.button.showFiles')}
          </Button>
        </Grid>
        {isPrivileged(user, 'STAFF') &&
          ['AS_1', 'Gesamtpalette Terminal W'].includes(infoData?.article?.name) && (
            <Grid item xs={12}>
              <MUIButton
                id="showRequestDetailsButton"
                fullWidth
                variant="outlined"
                sx={{ textTransform: 'none' }}
                onClick={() => {
                  history.push(`contracts/requests/${getPKfromSelf(infoData.self)}`)
                }}
              >
                {t('common:interaction.button.showDetails')}
              </MUIButton>
            </Grid>
          )}
      </Grid>
    </>
  )
}

export default function RequestInfoPanel(props: Props) {
  /* -------------------------- Non state data start -------------------------- */
  const { open, setOpen, setOpenFilesDrawer, infoData, title = '', disabled = false } = props
  const { t } = useTranslation()
  /* --------------------------- Non state data end --------------------------- */

  /* -------------------------------------------------------------------------- */
  /*                              Render Component                              */
  /* -------------------------------------------------------------------------- */

  return (
    <InfoPanel
      open={open}
      setOpen={setOpen}
      label={title || t('common:content.label.requestInformation')}
      allowEdit={false}
      disabled={disabled}
    >
      <RequestInfoPanelContent
        setOpenFilesDrawer={setOpenFilesDrawer}
        infoData={infoData}
      />
    </InfoPanel>
  )
}
